import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Restore as RestoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useQuery } from '@tanstack/react-query';

// Import real composer components
import { TemplateSelector } from '../../components/deliverables/composer/TemplateSelector';
import { SectionsStep } from '../../components/deliverables/composer/steps/SectionsStep';
import { AssetsStep } from '../../components/deliverables/composer/steps/AssetsStep';
import { PreviewStep } from '../../components/deliverables/composer/steps/PreviewStep';
import { GenerateStep } from '../../components/deliverables/composer/GenerateStep';
import {
  DeliverableTemplate,
  TemplateSection,
  TemplateControl,
  ComposerAssetData,
} from '@horwath-htl/shared-types';

// Step labels for the workflow
const STEPS = [
  'Select Template',
  'Configure Sections',
  'Create/Map Assets',
  'Review & Preview',
  'Generate Document',
];

// Types for the builder state
interface BuilderState {
  currentStep: number;
  selectedTemplateId: string | null;
  selectedTemplateName: string;
  selectedSections: string[];
  assetMappings: ComposerAssetData[];
  generationStatus: 'idle' | 'generating' | 'completed' | 'error';
  lastSavedAt: string | null;
}

interface Deliverable {
  id: number;
  name: string;
  description?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

// Session Recovery Dialog Component
interface SessionRecoveryDialogProps {
  open: boolean;
  savedState: BuilderState | null;
  onResume: () => void;
  onStartFresh: () => void;
}

const SessionRecoveryDialog: React.FC<SessionRecoveryDialogProps> = ({
  open,
  savedState,
  onResume,
  onStartFresh,
}) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <RestoreIcon color="primary" />
        Resume Previous Session?
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          A previous session was found for this deliverable. Would you like to resume where you left off or start fresh?
        </DialogContentText>
        {savedState && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Last Step:</strong> {STEPS[savedState.currentStep] || 'Unknown'}
            </Typography>
            <Typography variant="body2">
              <strong>Last Saved:</strong> {formatDate(savedState.lastSavedAt)}
            </Typography>
            {savedState.selectedTemplateId && (
              <Typography variant="body2">
                <strong>Template Selected:</strong> Yes
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onStartFresh} color="inherit">
          <RefreshIcon sx={{ mr: 1 }} />
          Start Fresh
        </Button>
        <Button onClick={onResume} variant="contained" color="primary">
          <RestoreIcon sx={{ mr: 1 }} />
          Resume Session
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main DeliverableBuilderPage Component
export const DeliverableBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { deliverableId } = useParams<{ deliverableId: string }>();
  const { authenticatedFetch } = useAuthenticatedFetch();

  // Deliverable data
  const [deliverable, setDeliverable] = useState<Deliverable | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Builder state
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('');
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [assetMappings, setAssetMappings] = useState<ComposerAssetData[]>([]);
  const [generationStatus, setGenerationStatus] = useState<'idle' | 'generating' | 'completed' | 'error'>('idle');
  const [generatedDocumentId, setGeneratedDocumentId] = useState<string | null>(null);

  // Parse deliverableId to number
  const deliverableIdNum = deliverableId ? parseInt(deliverableId, 10) : 0;

  // Fetch template details when a template is selected
  const {
    data: templateData,
    isLoading: templateLoading,
  } = useQuery({
    queryKey: ['template', selectedTemplateId],
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/deliverables/templates/${selectedTemplateId}`);
      if (!response?.ok) {
        throw new Error('Failed to fetch template details');
      }
      return await response.json();
    },
    enabled: !!selectedTemplateId,
  });

  const template: DeliverableTemplate | undefined = templateData?.template;

  // Extract sections and controls from template
  const allSections: TemplateSection[] = useMemo(() => {
    return template?.sections_config?.sections || [];
  }, [template]);

  const allControls: TemplateControl[] = useMemo(() => {
    return template?.controls_config?.controls || [];
  }, [template]);

  // Session recovery state
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const [savedState, setSavedState] = useState<BuilderState | null>(null);

  // Auto-save state
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    event.preventDefault();
    navigate(path);
  };

  // Fetch deliverable details
  const fetchDeliverable = useCallback(async () => {
    if (!deliverableId) return;

    try {
      const response = await authenticatedFetch(`/api/deliverables/${deliverableId}`);

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setDeliverable(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch deliverable');
      }
    } catch (err) {
      console.error('Error fetching deliverable:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch deliverable');
    }
  }, [authenticatedFetch, deliverableId]);

  // Fetch saved session state
  const fetchSessionState = useCallback(async () => {
    if (!deliverableId) return;

    try {
      const response = await authenticatedFetch(`/api/deliverables/${deliverableId}/state`);

      if (!response) {
        return; // No response, proceed without recovery
      }

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setSavedState(result.data);
          setShowRecoveryDialog(true);
        }
      }
      // If 404 or other error, just proceed - no saved state exists
    } catch (err) {
      console.error('Error fetching session state:', err);
      // Don't show error - just proceed without recovery
    }
  }, [authenticatedFetch, deliverableId]);

  // Save current state
  const saveState = useCallback(async () => {
    if (!deliverableId || isSaving) return;

    setIsSaving(true);
    try {
      const stateToSave: BuilderState = {
        currentStep,
        selectedTemplateId,
        selectedTemplateName,
        selectedSections,
        assetMappings,
        generationStatus,
        lastSavedAt: new Date().toISOString(),
      };

      const response = await authenticatedFetch(`/api/deliverables/${deliverableId}/save-state`, {
        method: 'POST',
        body: JSON.stringify(stateToSave),
      });

      if (response && response.ok) {
        setLastSavedAt(stateToSave.lastSavedAt);
      }
    } catch (err) {
      console.error('Error saving state:', err);
    } finally {
      setIsSaving(false);
    }
  }, [authenticatedFetch, deliverableId, currentStep, selectedTemplateId, selectedTemplateName, selectedSections, assetMappings, generationStatus, isSaving]);

  // Resume session from saved state
  const handleResumeSession = useCallback(() => {
    if (savedState) {
      setCurrentStep(savedState.currentStep);
      setSelectedTemplateId(savedState.selectedTemplateId);
      setSelectedTemplateName(savedState.selectedTemplateName || '');
      setSelectedSections(savedState.selectedSections);
      setAssetMappings(savedState.assetMappings);
      setGenerationStatus(savedState.generationStatus);
      setLastSavedAt(savedState.lastSavedAt);
    }
    setShowRecoveryDialog(false);
  }, [savedState]);

  // Start fresh session
  const handleStartFresh = useCallback(() => {
    setCurrentStep(0);
    setSelectedTemplateId(null);
    setSelectedTemplateName('');
    setSelectedSections([]);
    setAssetMappings([]);
    setGenerationStatus('idle');
    setGeneratedDocumentId(null);
    setLastSavedAt(null);
    setShowRecoveryDialog(false);
  }, []);

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchDeliverable();
      await fetchSessionState();
      setLoading(false);
    };

    loadData();
  }, [fetchDeliverable, fetchSessionState]);

  // Set up auto-save interval (60 seconds)
  useEffect(() => {
    autoSaveIntervalRef.current = setInterval(() => {
      saveState();
    }, 60000); // 60 seconds

    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, [saveState]);

  // Validation for each step
  const isStepValid = useCallback((step: number): boolean => {
    switch (step) {
      case 0: // Template selection
        return selectedTemplateId !== null;
      case 1: // Sections configuration
        return selectedSections.length > 0;
      case 2: // Asset mappings
        return true; // Allow proceeding even without mappings
      case 3: // Preview
        return true;
      case 4: // Generate
        return true;
      default:
        return false;
    }
  }, [selectedTemplateId, selectedSections]);

  // Handle step navigation
  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && isStepValid(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step: number) => {
    // Allow clicking on any previous step
    if (step <= currentStep) {
      setCurrentStep(step);
    }
  };

  // Handle template selection
  const handleTemplateSelect = (templateId: string | null, templateName: string) => {
    setSelectedTemplateId(templateId);
    setSelectedTemplateName(templateName);
    // Reset sections when template changes
    if (templateId !== selectedTemplateId) {
      setSelectedSections([]);
      setAssetMappings([]);
    }
  };

  // Handle sections change
  const handleSectionsChange = (sections: string[]) => {
    setSelectedSections(sections);
  };

  // Handle asset mappings change (from AssetsStep)
  const handleMappingsChange = (mappings: { controlId: string; assetId?: string; content?: any }[]) => {
    // Convert to ComposerAssetData format
    const composerMappings: ComposerAssetData[] = mappings.map(m => ({
      controlId: m.controlId,
      assetId: m.assetId,
      content: m.content,
    }));
    setAssetMappings(composerMappings);
  };

  // Handle document generation complete
  const handleGenerationComplete = (documentId: string) => {
    setGeneratedDocumentId(documentId);
    setGenerationStatus('completed');
  };

  // Handle step navigation from PreviewStep
  const handleNavigateToStep = (step: number) => {
    // Convert 1-based step from PreviewStep to 0-based
    setCurrentStep(step - 1);
  };

  // Format last saved time
  const formatLastSaved = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;

    return date.toLocaleTimeString();
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <TemplateSelector
            deliverableId={deliverableIdNum}
            selectedTemplateId={selectedTemplateId}
            onTemplateSelect={handleTemplateSelect}
          />
        );
      case 1:
        if (!selectedTemplateId) {
          return (
            <Alert severity="warning">
              Please select a template first before configuring sections.
            </Alert>
          );
        }
        if (templateLoading) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          );
        }
        return (
          <SectionsStep
            templateId={selectedTemplateId}
            selectedSections={selectedSections}
            onSectionsChange={handleSectionsChange}
          />
        );
      case 2:
        if (!selectedTemplateId || !template) {
          return (
            <Alert severity="warning">
              Please select a template and sections first.
            </Alert>
          );
        }
        return (
          <AssetsStep
            deliverableId={deliverableIdNum}
            templateId={selectedTemplateId}
            selectedSections={selectedSections}
            template={{
              sections_config: template.sections_config,
              controls_config: template.controls_config,
            }}
            onMappingsChange={handleMappingsChange}
          />
        );
      case 3:
        if (!selectedTemplateId) {
          return (
            <Alert severity="warning">
              Please complete the previous steps first.
            </Alert>
          );
        }
        return (
          <PreviewStep
            deliverableId={deliverableIdNum}
            templateId={selectedTemplateId}
            templateName={selectedTemplateName}
            templateType={template?.type || 'personal'}
            allSections={allSections}
            selectedSections={selectedSections}
            allControls={allControls}
            mappings={assetMappings}
            onNavigateToStep={handleNavigateToStep}
          />
        );
      case 4:
        if (!selectedTemplateId) {
          return (
            <Alert severity="warning">
              Please complete the previous steps first.
            </Alert>
          );
        }
        return (
          <GenerateStep
            deliverableId={deliverableIdNum}
            templateId={selectedTemplateId}
            selectedSections={selectedSections}
            onGenerationComplete={handleGenerationComplete}
          />
        );
      default:
        return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/deliverables')}>
          Back to Deliverables
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link
          color="inherit"
          href="/"
          onClick={(e) => handleBreadcrumbClick(e, '/')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          href="/deliverables"
          onClick={(e) => handleBreadcrumbClick(e, '/deliverables')}
          sx={{
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          Deliverables
        </Link>
        <Typography color="text.primary">
          {deliverable?.name || 'Builder'}
        </Typography>
      </Breadcrumbs>

      {/* Main Container */}
      <Paper elevation={1} sx={{ p: 3 }}>
        {/* Header with Title and Auto-save Indicator */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {deliverable?.name || 'Deliverable Builder'}
          </Typography>
          {lastSavedAt && (
            <Typography variant="caption" color="text.secondary">
              {isSaving ? 'Saving...' : `Last saved: ${formatLastSaved(lastSavedAt)}`}
            </Typography>
          )}
        </Box>

        {/* Stepper */}
        <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
          {STEPS.map((label, index) => (
            <Step key={label} completed={index < currentStep}>
              <StepButton
                onClick={() => handleStepClick(index)}
                disabled={index > currentStep}
              >
                <StepLabel>{label}</StepLabel>
              </StepButton>
            </Step>
          ))}
        </Stepper>

        {/* Step Content Area */}
        <Box sx={{ minHeight: 400, mb: 3 }}>
          {renderStepContent()}
        </Box>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={currentStep === 0}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>

          <Box>
            {/* GenerateStep (step 4) handles its own buttons, so hide Next on that step */}
            {currentStep < STEPS.length - 1 && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStepValid(currentStep)}
                endIcon={<ArrowForwardIcon />}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Session Recovery Dialog */}
      <SessionRecoveryDialog
        open={showRecoveryDialog}
        savedState={savedState}
        onResume={handleResumeSession}
        onStartFresh={handleStartFresh}
      />
    </Container>
  );
};
